<template>
  <header
    class="header w-full py-4 px-6 bg-transparent fixed top-0 left-0 z-[1000]"
    :class="{ scrolled: isScrolled }"
  >
    <div class="mobile-alignment-wrapper">
      <div class="header-container">
        <!-- Logo Section -->
        <div class="logo-section flex items-center gap-2">
          <picture>
            <source srcset="/full-icon.webp" type="image/webp" />
            <img src="/full-icon.png" alt="Logo" class="logo-image h-[120px] w-auto object-contain" width="120" height="120" loading="eager" fetchpriority="high" />
          </picture>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons flex items-center gap-3 ml-auto mr-6">
          <!-- Dashboard Button - Show for authenticated users who can access dashboard -->
          <button
            v-if="isAuthenticated && canAccessDashboard"
            class="btn btn-dashboard py-[10px] px-[30px] rounded-[24px] text-[16px] font-bold font-sans cursor-pointer transition-all duration-200 border-[3px] border-[#FCBB02] bg-[#FCBB02] text-black hover:bg-black hover:text-white hover:border-black"
            @click="navigateToDashboard"
          >
            Dashboard
          </button>
          
          <div class="relative">
            <button
              class="btn btn-login py-[10px] px-[50px] rounded-[24px] text-[18px] font-bold font-sans cursor-pointer transition-all duration-200 border-[3px] border-black bg-black text-white hover:bg-[#FCBB02] hover:text-black hover:border-black"
              :class="{ active: isLoginRoute }"
              @click="navigateToLogin"
            >
              {{ isAuthenticated ? "Log Out" : "Log In" }}
            </button>
            
            <!-- Warning indicator for students with document_issue status - Hide on application-status page -->
            <div
              v-if="isAuthenticated && userType === 'student' && studentStatus === 'document_issue' && route.name !== 'student-application-status'"
              class="absolute -top-1 -right-1 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center border-2 border-white shadow-lg"
              title="Document resubmission required"
            >
              <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Mobile Hamburger Menu -->
        <button
          class="mobile-menu-toggle hidden flex-col justify-around w-[25px] h-[25px] bg-transparent border-0 cursor-pointer p-0"
          @click="toggleMobileMenu"
        >
          <span
            class="hamburger-line w-[25px] h-[3px] bg-black rounded transition-all duration-300"
          ></span>
          <span
            class="hamburger-line w-[25px] h-[3px] bg-black rounded transition-all duration-300"
          ></span>
          <span
            class="hamburger-line w-[25px] h-[3px] bg-black rounded transition-all duration-300"
          ></span>
        </button>
      </div>

      <!-- Mobile Navigation Menu -->
      <div
        class="mobile-nav hidden absolute top-full left-0 right-0 bg-white border-[3px] border-black rounded-b-[50px] shadow-[8px_8px_0_#000] z-[999]"
        :class="{ active: isMobileMenuOpen }"
      >
        <div class="mobile-nav-content p-5 flex flex-col gap-[15px]">
          <router-link
            to="/"
            class="mobile-nav-link text-[18px] font-medium text-[#666666] no-underline py-3 border-b-2 border-[#f0f0f0] transition-all duration-300"
            @click="closeMobileMenu"
            >Home</router-link
          >
          <router-link
            to="/about"
            class="mobile-nav-link text-[18px] font-medium text-[#666666] no-underline py-3 border-b-2 border-[#f0f0f0] transition-all duration-300"
            @click="closeMobileMenu"
            >About</router-link
          >
          <router-link
            :to="{ name: 'register-role' }"
            class="mobile-nav-link text-[18px] font-medium text-[#666666] no-underline py-3 border-b-2 border-[#f0f0f0] transition-all duration-300"
            @click="closeMobileMenu"
            >Register</router-link
          >
          <!-- Dashboard Link - Show for authenticated users who can access dashboard -->
          <button
            v-if="isAuthenticated && canAccessDashboard"
            class="mobile-nav-link text-[18px] font-medium text-[#666666] no-underline py-3 border-b-2 border-[#f0f0f0] transition-all duration-300 text-left bg-transparent border-0 cursor-pointer"
            @click="navigateToDashboard"
          >
            Dashboard
          </button>
          <router-link
            :to="{ name: 'login' }"
            class="mobile-nav-link text-[18px] font-medium text-[#666666] no-underline py-3"
            @click="navigateToLogin"
            >{{ isAuthenticated ? "Logout" : "Login" }}</router-link
          >
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { auth, onAuthStateChanged, signOut, db } from "@/firebase";
import { doc, getDoc } from "firebase/firestore";
import { useStudentStatus } from "@/composables/useStudentStatus"; // Added for warning indicator
import "@/assets/main.css";

// Router
const router = useRouter();
const route = useRoute();

// Authentication state
const isAuthenticated = ref(false);
const currentUser = ref<any>(null);
const userType = ref<string | null>(null);
const applicationStatus = ref<string | null>(null);

// Student status for warning indicator
const { status: studentStatus } = useStudentStatus();

// Mobile menu state
const isMobileMenuOpen = ref(false);

// Scroll state
const isScrolled = ref(false);

// Add scroll event listener
const handleScroll = () => {
  isScrolled.value = window.scrollY > 10;
};

// Set up authentication state listener
let unsubscribeAuth: (() => void) | null = null;

// Set up and clean up scroll event listener and auth state listener
onMounted(() => {
  window.addEventListener("scroll", handleScroll, { passive: true });

  // Listen for authentication state changes
  unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
    isAuthenticated.value = !!user;
    currentUser.value = user;
    
    // Fetch user type and application status if user is authenticated
    if (user) {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          userType.value = userData.userType || null;
          applicationStatus.value = userData.applicationStatus || null;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        userType.value = null;
        applicationStatus.value = null;
      }
    } else {
      userType.value = null;
      applicationStatus.value = null;
    }
  });
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
  if (unsubscribeAuth) {
    unsubscribeAuth();
  }
});

// Check if current route is register-related
const isRegisterRoute = computed(() => {
  const path = route.path.toLowerCase();
  return (
    path.includes("/student-register") ||
    path.includes("/tutor-register") ||
    path.includes("/register") ||
    path.includes("register") ||
    path.includes("/email-verification")
  );
});

// Check if current route is login-related
const isLoginRoute = computed(() => {
  const path = route.path.toLowerCase();
  return path.includes("/login") || path.includes("login");
});

// Check if user can access dashboard based on their status
const canAccessDashboard = computed(() => {
  if (!isAuthenticated.value) return false;
  
  // Don't show dashboard button on rejection pages
  if (route.name === 'student-rejection') {
    return false;
  }
  
  // Don't show dashboard button for rejected tutors on application status page
  if (route.name === 'tutor-application-status' && applicationStatus.value === 'rejected') {
    return false;
  }
  
  // Students must be approved to access dashboard
  if (userType.value === 'student') {
    return applicationStatus.value === 'approved' || 
           applicationStatus.value === 'active' || 
           applicationStatus.value === 'Active';
  }
  
  // Tutors and admins can always see the button (tutors have their own guards)
  return true;
});

// Navigation functions
const navigateToRegisterRole = () => {
  router.push({ name: "register-role" });
  closeMobileMenu();
};

const navigateToStudentRegister = () => {
  router.push({ name: "student-register" });
  closeMobileMenu();
};

const navigateToDashboard = () => {
  if (!isAuthenticated.value || !userType.value) {
    // If not authenticated or no user type, redirect to login
    router.push({ name: "login" });
    closeMobileMenu();
    return;
  }

  // Navigate to appropriate dashboard based on user type
  switch (userType.value.toLowerCase()) {
    case 'student':
      router.push({ name: "student-sessions" });
      break;
    case 'tutor':
      router.push({ name: "tutor-sessions" });
      break;
    case 'validator':
      router.push({ name: "validator-placeholder" });
      break;
    case 'admin':
    case 'superadmin':
      router.push({ name: "admin-dashboard" });
      break;
    default:
      // Fallback to login if user type is unknown
      router.push({ name: "login" });
  }
  closeMobileMenu();
};

const navigateToLogin = async () => {
  if (isAuthenticated.value) {
    // User is logged in, perform logout
    try {
      await signOut(auth);
      
      // Clear all storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Use router.replace() to prevent back button navigation after logout
      router.replace({ name: "login" });
      closeMobileMenu();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  } else {
    // User is not logged in, navigate to login page
    router.push({ name: "login" });
    closeMobileMenu();
  }
};

// Toggle mobile menu
const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

// Close mobile menu
const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};
</script>

<style scoped>
body {
  padding-top: 120px;
}

.header {
  width: 100%;
  padding: 16px 24px;
  background: transparent;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
  transform: translateZ(0);
  will-change: transform;
}

/* Mobile Alignment Wrapper - matches buttons-container approach */
.mobile-alignment-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
}

.header-container {
  display: flex;
  align-items: center;
  width: 80em;
  height: 6em;
  margin: 0 auto;
  margin-top: 1.25em;
  background: white;
  border-radius: 50px;
  padding: 12px 24px;
  border: 3px solid #000;
  box-shadow: 8px 8px 0px #000;
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

/* Logo Section */
.logo-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.logo-image {
  height: 120px;
  width: auto;
  object-fit: contain;
}

.logo-icon {
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.logo-text {
  font-size: 20px;
  font-weight: 600;
  color: #000;
  letter-spacing: -0.02em;
}

/* Navigation */
.navigation {
  display: flex;
  align-items: center;
  gap: 32px;
  margin-left: auto;
  margin-right: 24px;
}

.nav-link {
  font-size: 18px;
  font-weight: 500;
  color: #666;
  text-decoration: none;
  padding: 8px 0;
  position: relative;
  transition: all 0.2s ease;
}

.nav-link:hover {
  color: #000;
  font-weight: 700;
  background: none !important;
}

.nav-link:hover::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background: #000;
  border-radius: 2px;
}

.nav-link.active {
  font-weight: 700;
  color: #000;
}

.nav-link.active::after {
  content: "";
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 5px;
  background: #000;
  border-radius: 10px;
}

/* Ensure no background colors on any nav-link states */
.nav-link,
.nav-link:hover,
.nav-link:focus,
.nav-link:active,
.nav-link.active {
  background: transparent !important;
  background-color: transparent !important;
}

/* Mobile Hamburger Menu */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: space-around;
  width: 25px;
  height: 25px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
}

.hamburger-line {
  width: 25px;
  height: 3px;
  background: #000;
  border-radius: 2px;
  transition: all 0.3s ease;
}

/* Mobile Navigation */
.mobile-nav {
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 3px solid #000;
  border-top: none;
  border-radius: 0 0 50px 50px;
  box-shadow: 8px 8px 0px #000;
  z-index: 999;
}

.mobile-nav.active {
  display: block;
}

.mobile-nav-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.mobile-nav-link {
  font-size: 18px;
  font-weight: 500;
  color: #666;
  text-decoration: none;
  padding: 12px 0;
  border-bottom: 2px solid #f0f0f0;
  transition: all 0.3s ease;
}

.mobile-nav-link:hover,
.mobile-nav-link.router-link-active {
  color: #000;
  font-weight: 700;
  border-bottom-color: #000;
}

.mobile-nav-link:last-child {
  border-bottom: none;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn {
  padding: 10px 40px;
  border-radius: 24px;
  font-size: 18px;
  font-weight: 700;
  font-family: "Inter", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;
  min-width: 120px;
}

.btn-register {
  background: white;
  color: #000;
  border: 3px solid #000;
}

.btn-register:hover,
.btn-register:active,
.btn-register.active {
  background: #fcbb02;
  color: #000;
  border-color: #000;
}

.btn-login {
  background: #000;
  color: white;
  border: 3px solid #000;
}

.btn-login:hover,
.btn-login:active,
.btn-login.active {
  background: #fcbb02;
  color: #000;
  border-color: #000;
}

/* Blur Effect for Header - Always active for better performance */
.header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.1);
  z-index: -1;
}

/* Optional: Add subtle opacity change on scroll for visual feedback */
.header.scrolled::before {
  background-color: rgba(255, 255, 255, 0.15);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .header-container {
    width: 90%;
  }
}

/* Mobile Alignment - matches buttons-container exactly */
@media (max-width: 768px) {
  .mobile-alignment-wrapper {
    width: 90%;
    max-width: 400px;
    margin: 0 auto;
  }

  .header {
    padding: 0;
    justify-content: flex-start;
  }

  .header-container {
    width: 100%;
    max-width: none;
    height: 4.5em;
    padding: 8px 20px;
    margin: 1em 0 0 0;
    box-shadow: 6px 6px 0px #000;
  }

  .logo-image {
    height: 80px;
  }

  .navigation {
    gap: 20px;
    margin-right: 16px;
  }

  .nav-link {
    font-size: 16px;
  }

  .nav-link:hover::after,
  .nav-link.active::after {
    height: 4px;
    border-radius: 8px;
  }

  .btn {
    padding: 8px 30px;
    font-size: 16px;
    min-width: 120px;
  }
}

@media (max-width: 600px) {
  .mobile-alignment-wrapper {
    width: 90%;
    max-width: 400px;
    margin: 0 auto;
  }

  .header {
    padding: 0;
    justify-content: flex-start;
  }

  .header-container {
    width: 100%;
    max-width: none;
    height: 3em;
    padding: 6px 12px;
    margin: 0.5em 0 0 0;
    box-shadow: 5px 5px 0px #000;
  }

  .logo-image {
    height: 60px;
  }

  .navigation {
    gap: 16px;
    margin-right: 12px;
  }

  .nav-link {
    font-size: 14px;
  }

  .btn {
    padding: 6px 25px;
    font-size: 14px;
    min-width: 100px;
  }
}

@media (max-width: 480px) {
  .mobile-alignment-wrapper {
    width: 85%;
    max-width: 350px;
    margin: 0 auto;
  }

  .header {
    padding: 0;
    justify-content: flex-start;
  }

  .header-container {
    width: 100%;
    max-width: none;
    height: 3em;
    padding: 4px 8px;
    margin: 0.5em 0 0 0;
    box-shadow: 4px 4px 0px #000;
    justify-content: space-between;
    position: relative;
  }

  .logo-image {
    height: 45px;
  }

  .navigation,
  .action-buttons {
    display: none;
  }

  .mobile-menu-toggle {
    display: flex;
  }

  .mobile-nav {
    margin-top: 0.5em;
    box-shadow: 4px 4px 0px #000;
  }

  .mobile-nav-link {
    font-size: 16px;
    padding: 10px 0;
  }
}

@media (max-width: 414px) {
  .mobile-alignment-wrapper {
    width: 85%;
    max-width: 320px;
    margin: 0 auto;
  }

  .header {
    padding: 0;
    justify-content: flex-start;
  }

  .header-container {
    width: 100%;
    max-width: none;
    height: 2.5em;
    padding: 2px 6px;
    margin: 0.3em 0 0 0;
    box-shadow: 3px 3px 0px #000;
  }

  .logo-image {
    height: 32px;
  }
}

@media (max-width: 360px) {
  .mobile-alignment-wrapper {
    width: 80%;
    max-width: 280px;
    margin: 0 auto;
  }

  .header {
    padding: 0;
    justify-content: flex-start;
  }

  .header-container {
    width: 100%;
    max-width: none;
    height: 2.5em;
    padding: 2px 6px;
    margin: 0.3em 0 0 0;
    box-shadow: 3px 3px 0px #000;
  }

  .logo-image {
    height: 35px;
  }
}
</style>
